import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import { useAuth } from "../../Context/authContext";
import { Typography } from "../../Components/Typography";
import Avatar from "../../Components/Avatar";
import {
  getIdeasByUserId,
  getTasksByUserId,
  getMessagesByUserId,
  deleteMessageSent,
} from "../../Utils/apiServices";
import CorporateSponsorDashboard from "../CorporateSponsorDashboard";
import EcosystemPartnerDashboard from "../EcosystemPartnerDashboard";
import InvestorDashboard from "../InvestorDashboard";

const getStatsForRole = (role) => {
  switch (role) {
    case "admin":
      return [
        { label: "Total Users", value: 100 },
        { label: "Total Ideas", value: 50 },
        { label: "Total Engagements", value: 70 },
      ];
    case "mentor":
      return [
        { label: "Ideas Mentored", value: 12 },
        { label: "Active Ventures", value: 5 },
        { label: "Startups Launched", value: 1 },
      ];
    case "student":
      return null; // Fetched dynamically
    case "donor":
      return [
        { label: "Donations", value: 8 },
        { label: "Students Impacted", value: 24 },
        { label: "Active Recurring", value: 2 },
      ];
    case "investor":
      return [
        { label: "Startups Reviewed", value: 10 },
        { label: "Investments", value: 3 },
        { label: "Follow-ups", value: 6 },
      ];
    default:
      return [
        { label: "Ideas Mentored", value: 0 },
        { label: "Active Ventures", value: 0 },
        { label: "Startups Launched", value: 0 },
      ];
  }
};

const getDateString = () => {
  return new Date().toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const Dashboard = () => {
  const { user } = useAuth();
  const [studentStats, setStudentStats] = useState({
    myIdeas: 0,
    tasksDue: 0,
    mentorSessions: 0,
  });
  const [studentStatsLoading, setStudentStatsLoading] = useState(true);
  const [mentorMessages, setMentorMessages] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [clearingId, setClearingId] = useState(null);
  const notifRef = useRef(null);

  // Fetch any messages sent to this student (mentor notifications)
  useEffect(() => {
    if (user?.role !== "student" || !user?.id) return;
    let cancelled = false;
    const fetchMessages = async () => {
      try {
        const res = await getMessagesByUserId(user.id);
        if (cancelled) return;
        const data = res?.data?.data ?? res?.data ?? [];
        setMentorMessages(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setMentorMessages([]);
      }
    };
    fetchMessages();
    return () => { cancelled = true; };
  }, [user?.id, user?.role]);

  // Close the dropdown when clicking outside of it
  useEffect(() => {
    if (!notifOpen) return;
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [notifOpen]);

  // Clear a single notification: delete just that message from the DB
  const handleClearMessage = async (id) => {
    if (!id || clearingId) return;
    setClearingId(id);
    try {
      await deleteMessageSent(id);
      setMentorMessages((prev) => prev.filter((msg) => msg.id !== id));
    } catch (err) {
      // Leave the item in place so the student can retry
    } finally {
      setClearingId(null);
    }
  };

  useEffect(() => {
    if (user?.role !== "student" || !user?.id) {
      if (user?.role === "student") setStudentStatsLoading(false);
      return;
    }
    let cancelled = false;
    const fetchStudentStats = async () => {
      setStudentStatsLoading(true);
      try {
        const [ideasRes, tasksRes] = await Promise.all([
          getIdeasByUserId(user.id),
          getTasksByUserId(user.id),
        ]);
        if (cancelled) return;
        const ideasData = ideasRes?.data?.data ?? ideasRes?.data ?? [];
        const tasksData = tasksRes?.data?.data ?? tasksRes?.data ?? [];
        const ideasCount = Array.isArray(ideasData) ? ideasData.length : 0;
        const tasksCount = Array.isArray(tasksData) ? tasksData.length : 0;
        setStudentStats({
          myIdeas: ideasCount,
          tasksDue: tasksCount,
          mentorSessions: 0,
        });
      } catch (err) {
        if (!cancelled) {
          setStudentStats({ myIdeas: 0, tasksDue: 0, mentorSessions: 0 });
        }
      } finally {
        if (!cancelled) setStudentStatsLoading(false);
      }
    };
    fetchStudentStats();
    return () => { cancelled = true; };
  }, [user?.id, user?.role]);

  if (user?.role === "corporate_sponsor") return <CorporateSponsorDashboard />;
  if (user?.role === "ecosystem_partner") return <EcosystemPartnerDashboard />;
  if (user?.role === "investor") return <InvestorDashboard />;

  const stats =
    user?.role === "student"
      ? [
          { label: "My Ideas", value: studentStatsLoading ? "..." : studentStats.myIdeas },
          { label: "Tasks Due", value: studentStatsLoading ? "..." : studentStats.tasksDue },
          { label: "Mentor Sessions", value: 0 },
        ]
      : getStatsForRole(user?.role);

  const getDisplayName = () => {
    if (user?.first_name || user?.last_name) {
      return `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email || "Guest";
    }
    return user?.email || "Guest";
  };

  return (
    <section className="flex flex-col w-full h-auto rounded-[40px] border-tl  pt-8 pb-12 gap-8 bg-primary">
      <div className="flex w-auto h-auto px-8 items-start justify-between gap-4">
        <div className="flex items-start justify-start gap-4">
          <Avatar
            src={user?.profile_picture_url}
            firstName={user?.first_name}
            lastName={user?.last_name}
            email={user?.email}
            alt="User avatar"
            size="w-16 h-16"
            showBorder={true}
            className="border-slate-200"
          />
          <div className="flex flex-col gap-1 items-start justify-start">
            <p className="text-xl font-semibold text-primary-900">
              Hello, {getDisplayName()}
            </p>
            <p className="text-sm text-tertiary-600">{getDateString()}</p>
          </div>
        </div>

        {user?.role === "student" && (
          <div className="relative" ref={notifRef}>
            <button
              type="button"
              onClick={() => setNotifOpen((open) => !open)}
              aria-label="Notifications"
              className="relative h-11 w-11 rounded-full border border-slate-200 bg-primary flex items-center justify-center hover:bg-tertiary"
            >
              <Icon icon="lucide:bell" className="h-5 w-5 text-primary-900" />
              {mentorMessages.length > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-xs font-semibold flex items-center justify-center">
                  {mentorMessages.length}
                </span>
              )}
            </button>

            {notifOpen && (
              <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-2xl border border-slate-200 bg-primary shadow-lg z-20">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                  <Typography as="p" className="text-sm font-semibold text-primary-900">
                    Notifications
                  </Typography>
                  <span className="text-xs text-tertiary-600">
                    {mentorMessages.length} new
                  </span>
                </div>

                {mentorMessages.length === 0 ? (
                  <div className="px-4 py-6 text-center text-sm text-tertiary-600">
                    You're all caught up.
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {mentorMessages.map((msg) => (
                      <li
                        key={msg.id}
                        className="flex items-start justify-between gap-3 px-4 py-3 hover:bg-tertiary/40"
                      >
                        <div className="flex items-start gap-3 min-w-0">
                          <div className="h-8 w-8 shrink-0 rounded-full bg-tertiary flex items-center justify-center">
                            <Icon icon="lucide:mail" className="h-4 w-4 text-primary-900" />
                          </div>
                          <p className="text-sm text-primary-900 break-words">
                            {msg.message}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleClearMessage(msg.id)}
                          disabled={clearingId === msg.id}
                          aria-label="Clear notification"
                          className="shrink-0 rounded-full p-1 text-tertiary-600 hover:bg-tertiary hover:text-primary-900 disabled:opacity-50"
                        >
                          <Icon icon="lucide:x" className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className=" flex w-auto h-auto px-8 items-start justify-start gap-4 grid grid-cols-1 md:grid-cols-3">
        {stats.map((item) => (
          <div
            key={item.label}
            className="bg-primary border border-slate-200 rounded-2xl shadow-sm p-6 flex flex-col gap-3"
          >
            <div className="h-12 w-12 rounded-full bg-tertiary flex items-center justify-center">
              <Icon
                icon="lucide:trending-up"
                className="h-6 w-6 text-gray-500"
              />
            </div>
            <div className="flex flex-col gap-1 items-start justify-start">
              <Typography as="p" className="text-sm text-tertiary-600">
                {item.label}
              </Typography>
              <Typography as="h3" className="text-3xl font-bold text-primary-900">
                {item.value}
              </Typography>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Dashboard;
